import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/hooks/useAuth";
import { isAdmin as fetchIsAdmin } from "@/utils/roles.functions";
import { listSecurityAuditLog, type SecurityAuditRow } from "@/utils/audit.functions";
import {
  acknowledgeSecurityAlert,
  listSecurityAlerts,
  type SecurityAlertRow,
} from "@/utils/security-alerts.functions";
import { ShieldAlert, RefreshCw, Filter, BellRing, Check } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin-audit")({
  head: () => ({
    meta: [
      { title: "Security Audit — Admin — LendyTrack" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminAudit,
});

const EVENT_TYPES = [
  "auth.signin.failed_lockout",
  "auth.signin.success",
  "auth.signin.failed",
  "auth.signout",
  "auth.password.reset_requested",
  "payments.checkout.created",
  "payments.subscription.updated",
  "admin.role.granted",
  "admin.role.revoked",
  "admin.kyc.approved",
  "admin.kyc.rejected",
  "admin.message.status_changed",
  "admin.message.deleted",
  "admin.loan_listing.updated",
  "admin.invest_plan.updated",
  "rls.policy.changed",
  "access.suspicious",
  "access.unauthorized",
  "access.forbidden",
] as const;

function categoryOf(t: string): "auth" | "admin" | "access" | "payments" | "rls" | "other" {
  if (t.startsWith("auth.")) return "auth";
  if (t.startsWith("admin.")) return "admin";
  if (t.startsWith("access.")) return "access";
  if (t.startsWith("payments.")) return "payments";
  if (t.startsWith("rls.")) return "rls";
  return "other";
}

function toneOf(t: string): string {
  const c = categoryOf(t);
  if (c === "access" || t.endsWith("failed") || t.endsWith("failed_lockout")) {
    return "border-danger/40 bg-danger/10 text-danger";
  }
  if (c === "admin" || c === "rls") return "border-accent/40 bg-accent/10 text-accent";
  if (c === "payments") return "border-primary/40 bg-primary/10 text-primary";
  return "border-border bg-muted text-muted-foreground";
}

function AdminAudit() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [access, setAccess] = useState<"checking" | "allowed" | "denied">("checking");
  const [filter, setFilter] = useState<string>("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/auth" });
      return;
    }
    fetchIsAdmin()
      .then((r) => setAccess(r.isAdmin ? "allowed" : "denied"))
      .catch(() => setAccess("denied"));
  }, [user, authLoading, navigate]);

  const q = useQuery({
    queryKey: ["security_audit_log", filter],
    enabled: access === "allowed",
    queryFn: async (): Promise<SecurityAuditRow[]> => {
      const res = await listSecurityAuditLog({
        data: {
          limit: 200,
          ...(filter ? { event_type: filter as (typeof EVENT_TYPES)[number] } : {}),
        },
      });
      return res.rows;
    },
    refetchInterval: 30_000,
  });

  const qc = useQueryClient();
  const alertsQ = useQuery({
    queryKey: ["security_alerts"],
    enabled: access === "allowed",
    queryFn: async (): Promise<SecurityAlertRow[]> => {
      const res = await listSecurityAlerts({ data: { onlyUnacked: false, limit: 50 } });
      return res.rows;
    },
    refetchInterval: 30_000,
  });

  const ackAlert = async (id: string) => {
    try {
      await acknowledgeSecurityAlert({ data: { id } });
      toast.success("Alert acknowledged");
      qc.invalidateQueries({ queryKey: ["security_alerts"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to acknowledge");
    }
  };

  if (access !== "allowed") {
    return (
      <AppShell>
        <div className="mx-auto mt-20 max-w-md rounded-2xl border border-border bg-card p-10 text-center shadow-soft">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-danger/10 text-danger">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <h1 className="mt-4 font-display text-xl font-semibold">
            {access === "checking" ? "Verifying access…" : "Admins only"}
          </h1>
          {access === "denied" && (
            <Link
              to="/dashboard"
              className="mt-6 inline-block rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
            >
              Back to dashboard
            </Link>
          )}
        </div>
      </AppShell>
    );
  }

  const rows = q.data ?? [];
  const alerts = alertsQ.data ?? [];
  const unacked = alerts.filter((a) => !a.acknowledged_at);

  const sevTone = (s: SecurityAlertRow["severity"]) =>
    s === "critical"
      ? "border-danger/60 bg-danger/15 text-danger"
      : s === "high"
        ? "border-danger/40 bg-danger/10 text-danger"
        : s === "medium"
          ? "border-accent/40 bg-accent/10 text-accent"
          : "border-border bg-muted text-muted-foreground";

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin</div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Security audit log</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Authentication events, admin actions, payments, policy changes, and suspicious access attempts.
          </p>
        </div>
        <button
          onClick={() => q.refetch()}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold hover:bg-muted"
        >
          <RefreshCw className={`h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="mb-6 rounded-2xl border border-border bg-card shadow-soft">
        <div className="flex items-center justify-between border-b border-border p-5">
          <div className="flex items-center gap-3">
            <div
              className={`grid h-9 w-9 place-items-center rounded-full ${
                unacked.length > 0 ? "bg-danger/15 text-danger" : "bg-muted text-muted-foreground"
              }`}
            >
              <BellRing className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-accent">Alerts</div>
              <h2 className="font-display text-lg font-semibold">
                {unacked.length > 0
                  ? `${unacked.length} unacknowledged alert${unacked.length === 1 ? "" : "s"}`
                  : "No active alerts"}
              </h2>
            </div>
          </div>
          <span className="text-xs text-muted-foreground">{alerts.length} total · last 50</span>
        </div>
        {alerts.length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">
            Alerts appear here when 5+ suspicious/forbidden events occur within 15 minutes from the same actor or IP, or when an account is locked.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {alerts.slice(0, 10).map((a) => (
              <li key={a.id} className="flex flex-col gap-2 p-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3">
                  <span className={`mt-0.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${sevTone(a.severity)}`}>
                    {a.severity}
                  </span>
                  <div>
                    <div className="text-sm font-semibold">{a.summary}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {a.kind} · {new Date(a.created_at).toLocaleString()}
                      {a.ip_address ? ` · IP ${a.ip_address}` : ""}
                      {a.user_id ? ` · user ${a.user_id.slice(0, 8)}…` : ""}
                      {a.event_count > 1 ? ` · ${a.event_count} events` : ""}
                      {a.email_status === "sent"
                        ? " · email sent"
                        : a.email_status === "skipped"
                          ? " · email skipped"
                          : a.email_status === "failed"
                            ? " · email failed"
                            : " · email pending"}
                    </div>
                  </div>
                </div>
                {!a.acknowledged_at ? (
                  <button
                    onClick={() => ackAlert(a.id)}
                    className="inline-flex items-center gap-1.5 self-start rounded-md border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted md:self-auto"
                  >
                    <Check className="h-3.5 w-3.5" /> Acknowledge
                  </button>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    Ack'd {new Date(a.acknowledged_at).toLocaleString()}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>


      <div className="mb-4 flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-md border border-border bg-card px-3 py-2 text-sm"
        >
          <option value="">All events</option>
          {EVENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <span className="text-xs text-muted-foreground">{rows.length} entries</span>
      </div>

      <div className="rounded-2xl border border-border bg-card shadow-soft">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-5 py-3">When</th>
                <th className="px-5 py-3">Event</th>
                <th className="px-5 py-3">Actor</th>
                <th className="px-5 py-3">IP</th>
                <th className="px-5 py-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {q.isLoading && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {!q.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-muted-foreground">
                    No audit entries match the current filter.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border align-top hover:bg-muted/30">
                  <td className="px-5 py-3 whitespace-nowrap tabular-nums text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${toneOf(r.event_type)}`}
                    >
                      {r.event_type}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-muted-foreground">
                    {r.user_id ? r.user_id.slice(0, 8) + "…" : "—"}
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-muted-foreground">
                    {r.ip_address ?? "—"}
                  </td>
                  <td className="px-5 py-3">
                    {r.details && typeof r.details === "object" && Object.keys(r.details as object).length > 0 ? (
                      <pre className="max-w-md overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-muted p-2 text-[11px] text-muted-foreground">
                        {JSON.stringify(r.details, null, 2)}
                      </pre>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
